/* Jinja block openers.
 * Template text is emitted raw into JinjaSQL, where there is no escape that
 * keeps these inert, so a string containing one cannot be represented safely
 * in a templatized query.
 */
export const JINJA_BLOCK_OPENERS = ["{{", "{%", "{#"] as const;

/* Characters that let a column name escape the backticks we quote it with.
 *
 * A backtick closes the identifier early, so everything after it gets parsed as
 * expression grammar (comparisons, boolean logic, other dimensions) instead of
 * as a column name.
 *
 * The backslash is only for `CalcLexer`, which treats `\`` as an escape inside
 * an identifier, so a trailing one there eats the closing backtick. hex-sl's
 * parser reads a backslash literally, and that's what actually compiles to SQL,
 * so this half is just belt and braces. Downside is we drop filters on real
 * columns with a backslash in the name.
 */
const HQL_IDENTIFIER_BREAKOUT_CHARS = ["`", "\\"] as const;

/**
 * Whether `text` can be embedded in a templatized SQL query.
 */
export function isTemplateSafeText(text: string): boolean {
  return !JINJA_BLOCK_OPENERS.some((opener) => text.includes(opener));
}

/**
 * Whether `identifier` can be embedded in a templatized SQL query. Numbers
 * (positional references, e.g. `order by 2`) are always safe.
 */
export function isTemplateSafeIdentifier(identifier: string | number): boolean {
  return typeof identifier === "number" || isTemplateSafeText(identifier);
}

/**
 * Whether `identifier` can be embedded in HQL, which requires it to be safe
 * both as templatized SQL text and inside the backtick-quoted identifier HQL
 * filter expressions wrap it in.
 */
export function isHqlSafeIdentifier(identifier: string | number): boolean {
  if (typeof identifier === "number") {
    return true;
  }
  return (
    isTemplateSafeText(identifier) &&
    !HQL_IDENTIFIER_BREAKOUT_CHARS.some((char) => identifier.includes(char))
  );
}
